import { filterEmployeePayrollHistory } from "../utils/payrollHistory";

export const EMPTY_EMPLOYEE_PROFILE = {
  summary: {
    employeeId: "-",
    department: "-",
    location: "-",
    joinedOn: "-",
    completeness: "0%",
    completionSuggestions: [],
  },
  personal: [],
  contact: [],
  employment: [],
  payrollIdentifiers: [],
  addresses: [],
  emergencyContacts: [],
  insuranceDependents: [],
  bankAccounts: [],
  documents: [],
  documentUpload: {},
  documentUploadRequirements: [],
  hrmPermissions: {},
  editPermissions: {},
  lockedFields: [],
};

const parseJsonString = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const toArray = (value) => {
  const parsedValue = parseJsonString(value);
  if (parsedValue !== value) {
    return toArray(parsedValue);
  }

  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.data)) {
    return value.data;
  }
  if (value?.data && typeof value.data === "object") {
    return toArray(value.data);
  }
  if (value?.result && typeof value.result === "object") {
    return toArray(value.result);
  }
  if (Array.isArray(value?.records)) {
    return value.records;
  }
  if (Array.isArray(value?.items)) {
    return value.items;
  }
  if (Array.isArray(value?.users)) {
    return value.users;
  }
  if (Array.isArray(value?.employees)) {
    return value.employees;
  }
  if (Array.isArray(value?.people)) {
    return value.people;
  }
  if (Array.isArray(value?.balances)) {
    return value.balances;
  }
  if (Array.isArray(value?.requests)) {
    return value.requests;
  }
  if (Array.isArray(value?.credits)) {
    return value.credits;
  }
  if (Array.isArray(value?.holidays)) {
    return value.holidays;
  }
  if (Array.isArray(value?.logs)) {
    return value.logs;
  }
  if (Array.isArray(value?.expenses)) {
    return value.expenses;
  }
  if (Array.isArray(value?.directory)) {
    return value.directory;
  }
  if (Array.isArray(value?.projects)) {
    return value.projects;
  }
  if (Array.isArray(value?.accounts)) {
    return value.accounts;
  }
  if (Array.isArray(value?.tasks)) {
    return value.tasks;
  }
  if (Array.isArray(value?.entities)) {
    return value.entities;
  }
  if (Array.isArray(value?.projectEntities)) {
    return value.projectEntities;
  }
  if (Array.isArray(value?.slips)) {
    return value.slips;
  }
  if (Array.isArray(value?.salarySlips)) {
    return value.salarySlips;
  }
  if (Array.isArray(value?.assets)) {
    return value.assets;
  }
  if (Array.isArray(value?.allocations)) {
    return value.allocations;
  }
  if (Array.isArray(value?.policies)) {
    return value.policies;
  }
  if (Array.isArray(value?.reviews)) {
    return value.reviews;
  }
  if (Array.isArray(value?.responseJSON)) {
    return value.responseJSON;
  }
  const parsedResponseJson = parseJsonString(value?.responseJSON);
  if (Array.isArray(parsedResponseJson)) {
    return parsedResponseJson;
  }
  return [];
};

const getSourceValue = (source, key) => {
  if (source?.[key] !== undefined && source?.[key] !== null) {
    return source[key];
  }

  if (!key.includes(".") || !source || typeof source !== "object") {
    return undefined;
  }

  return key.split(".").reduce((current, item) => current?.[item], source);
};

const firstValue = (source, keys, fallback = "") => {
  for (const key of keys) {
    const value = getSourceValue(source, key);
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return fallback;
};

const canonicalCasePersonName = (name = "", email = "") => {
  const normalizedName = String(name || "").trim();
  const nameKey = normalizedName.toLowerCase();
  const emailKey = String(email || "").trim().toLowerCase();
  const shivAliases = new Set([
    "hsiv sharma",
    "shiv bagherwal",
    "shiv kumar bagherwal",
    "shiv sharma",
  ]);

  if (emailKey === "shiv@antrazal.com" || shivAliases.has(nameKey)) {
    return "Shiv Kumar Bagherwal";
  }
  return normalizedName;
};

const firstStringValue = (source, keys, fallback = "") => {
  const value = firstValue(source, keys, fallback);
  return value === undefined || value === null || typeof value === "object" ? fallback : String(value);
};

const toBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  const normalized = String(value).trim().toLowerCase();
  if (["true", "1", "yes", "active"].includes(normalized)) {
    return true;
  }
  if (["false", "0", "no", "inactive"].includes(normalized)) {
    return false;
  }
  return fallback;
};

const toNumber = (value) => {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const isLeaveType = (balance, type) =>
  String(balance?.label || balance?.leaveType || balance?.type || "")
    .toLowerCase()
    .includes(type);

const isLossOfPayBalance = (balance) => {
  const normalizedType = String(balance?.label || balance?.leaveType || balance?.type || "")
    .trim()
    .toLowerCase();
  return normalizedType === "lop" || normalizedType.includes("loss of pay");
};

const normalizeLeaveBalanceOverflow = (balance) => {
  const assigned = toNumber(balance.assigned);
  const used = toNumber(balance.used);
  const remaining = toNumber(balance.remaining);
  const usedOverflow = Math.max(used - assigned, 0);
  const negativeBalanceOverflow = Math.max(-remaining, 0);

  return Math.max(usedOverflow, negativeBalanceOverflow);
};

const applyEarnedLeaveStacking = (balances) => {
  let stackedOverflow = 0;
  const adjustedBalances = balances.map((balance) => {
    if (!isLeaveType(balance, "casual") && !isLeaveType(balance, "sick")) {
      return balance;
    }

    const overflow = normalizeLeaveBalanceOverflow(balance);
    stackedOverflow += overflow;

    return {
      ...balance,
      remaining: Math.max(toNumber(balance.remaining), 0),
      stackedOverflow: overflow,
    };
  });

  if (!stackedOverflow) {
    return adjustedBalances;
  }

  return adjustedBalances.map((balance) =>
    isLeaveType(balance, "earned")
      ? {
          ...balance,
          remaining: Math.max(toNumber(balance.remaining) - stackedOverflow, 0),
          stackedDeduction: stackedOverflow,
        }
      : balance
  );
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDatedCommentDate = (value = "") => {
  const match = value.match(/^(\d{1,2})-([A-Za-z]{3,9})-(\d{2}|\d{4})$/);
  if (!match) {
    return value;
  }

  const [, day, month, year] = match;
  const monthNames = {
    apr: "Apr",
    aug: "Aug",
    dec: "Dec",
    feb: "Feb",
    jan: "Jan",
    jul: "Jul",
    jun: "Jun",
    mar: "Mar",
    may: "May",
    nov: "Nov",
    oct: "Oct",
    sep: "Sep",
  };
  const numericYear = Number(year);
  const fullYear = year.length === 2 ? 2000 + numericYear : numericYear;

  return `${String(Number(day)).padStart(2, "0")} ${monthNames[month.toLowerCase().slice(0, 3)] || month} ${fullYear}`;
};

const cleanRichTextComment = (value = "") => {
  if (value === undefined || value === null) {
    return "";
  }

  const rawValue = String(value).trim();
  if (!rawValue) {
    return "";
  }
  if (!/<[a-z][\s\S]*>/i.test(rawValue) && !/&(?:[a-z]+|#\d+|#x[\da-f]+);/i.test(rawValue)) {
    return rawValue;
  }

  const valueWithBreaks = rawValue
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, "\n");
  const text =
    typeof window !== "undefined" && window.DOMParser
      ? new window.DOMParser().parseFromString(valueWithBreaks, "text/html").body.textContent || ""
      : valueWithBreaks.replace(/<[^>]+>/g, " ");
  const lines = text
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  const datedSections = [];
  let currentSection = null;

  lines.forEach((line) => {
    const dateMatch = line.match(/^ON\s+(\d{1,2}-[A-Za-z]{3,9}-\d{2,4})$/i);
    if (dateMatch) {
      if (currentSection?.lines.length) {
        datedSections.push(`${formatDatedCommentDate(currentSection.date)} - ${currentSection.lines.join(" ")}`);
      }
      currentSection = { date: dateMatch[1], lines: [] };
      return;
    }

    if (currentSection) {
      currentSection.lines.push(line);
      return;
    }

    datedSections.push(line);
  });

  if (currentSection?.lines.length) {
    datedSections.push(`${formatDatedCommentDate(currentSection.date)} - ${currentSection.lines.join(" ")}`);
  }

  return datedSections.length ? datedSections.join("\n") : lines.join("\n");
};

const formatDateKey = (value) => {
  if (!value) {
    return "";
  }
  const rawValue = String(value);
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const normalizeIsoDateKey = (value) => {
  if (!value) {
    return "";
  }

  const rawValue = String(value).trim();
  const isoMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) {
    return isoMatch[1];
  }

  const slashDateMatch = rawValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slashDateMatch) {
    return `${slashDateMatch[3]}-${String(slashDateMatch[2]).padStart(2, "0")}-${String(slashDateMatch[1]).padStart(2, "0")}`;
  }

  return formatDateKey(rawValue);
};

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }
  const rawValue = String(value);
  const date = new Date(rawValue.includes("T") ? rawValue : `${rawValue}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatTime = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeStatus = (value, fallback = "Pending") => {
  const status = String(value || fallback).trim() || fallback;
  const normalized = status.toLowerCase();
  if (normalized.includes("pending")) {
    return "Pending";
  }
  if (normalized.includes("approved")) {
    return "Approved";
  }
  if (normalized.includes("reject")) {
    return "Rejected";
  }
  if (normalized.includes("cancel")) {
    return "Cancelled";
  }
  return status;
};

const statusTone = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("approved")) {
    return "approved";
  }
  if (normalized.includes("pending")) {
    return "pending";
  }
  if (normalized.includes("reject")) {
    return "rejected";
  }
  if (normalized.includes("cancel")) {
    return "cancelled";
  }
  return "neutral";
};

export const normalizeUser = (user) => ({
  id: firstValue(user, ["id", "contactId", "employeeSalesforceId", "employeeId", "employee_id"], ""),
  contactId: firstValue(user, ["contactId", "employeeSalesforceId", "employeeId", "employee_id", "id"], ""),
  name: firstValue(user, ["name", "fullName", "Name"], "User").replace(/_/g, " "),
  email: firstValue(user, ["email", "Email"], ""),
  employeeType: firstValue(user, ["employeeType", "role", "type"], "Employee"),
  role: firstValue(user, ["employeeType", "role", "type"], "Employee"),
  employeeSalesforceId: firstValue(user, ["employeeSalesforceId", "contactId", "employeeId", "employee_id", "id"], ""),
  profileImageUrl: firstValue(user, ["profileImageUrl", "profilePhotoUrl", "profileImage", "picture", "imageUrl", "photoUrl", "Profile_Image_URL__c"], ""),
  isActive: firstValue(user, ["isActive", "is_active"], true),
});

export const normalizeUsers = (payload) => toArray(payload).map(normalizeUser);

const ATTENDANCE_HISTORY_START_DATE_KEY = "2026-05-01";

export const normalizeAttendance = (todayPayload, logsPayload) => {
  const today = todayPayload?.attendance || todayPayload?.today || todayPayload || {};
  const status = firstValue(today, ["status", "attendanceStatus"], "checked-out");
  const rawCheckIn = firstValue(today, ["checkInAt", "checkInDateTime", "checkInTime", "inTime", "checkIn", "Check_In__c"], "");
  const rawCheckOut = firstValue(today, ["checkOutAt", "checkOutDateTime", "checkOutTime", "outTime", "checkOut", "Check_Out__c"], "");
  const normalizedStatus = String(status || "").trim().toLowerCase().replace(/[_\s]+/g, "-");
  const isExplicitlyCheckedOut = ["checked-out", "checkout", "checkedout", "out", "closed", "completed"].includes(normalizedStatus);
  const todayRawDate = firstValue(today, ["loggedDate", "date", "attendanceDate", "Attendance_Date__c"], new Date());
  const todayDateKey = formatDateKey(todayRawDate) || formatDateKey(new Date());
  const logs = toArray(logsPayload).map((log, index) => {
    const rawLoggedDate = firstValue(log, ["loggedDate", "date", "attendanceDate", "Attendance_Date__c"]);
    const logRawCheckIn = firstValue(log, ["checkInAt", "checkInDateTime", "checkInTime", "inTime", "checkIn", "Check_In__c"], "");
    const logRawCheckOut = firstValue(log, ["checkOutAt", "checkOutDateTime", "checkOutTime", "outTime", "checkOut", "Check_Out__c"], "");

    return {
      id: firstValue(log, ["id", "Id"], `attendance-${index}`),
      rawLoggedDate,
      loggedDate: formatDate(rawLoggedDate),
      dateKey: formatDateKey(rawLoggedDate),
      attendanceCode: firstValue(log, ["attendanceCode", "code", "Attendance_Code__c", "Attendance_Status_Code__c", "Code__c"], ""),
      rawCheckIn: logRawCheckIn,
      rawCheckOut: logRawCheckOut,
      status: firstValue(log, ["status", "attendanceStatus", "Status__c"], ""),
      checkInTime: formatTime(logRawCheckIn),
      checkOutTime: formatTime(logRawCheckOut),
      totalHours: String(firstValue(log, ["totalHours", "hours", "Total_Hours__c"], "")),
    };
  }).filter((log) => !log.dateKey || log.dateKey >= ATTENDANCE_HISTORY_START_DATE_KEY);
  const openTodayLog = logs.find((log) => log.dateKey === todayDateKey && log.rawCheckIn && !log.rawCheckOut);
  const openLog = openTodayLog || logs.find((log) => log.rawCheckIn && !log.rawCheckOut);
  const activeRawDate = openLog?.rawLoggedDate || todayRawDate;
  const effectiveRawCheckIn = openLog?.rawCheckIn || rawCheckIn || "";
  const effectiveRawCheckOut = openLog ? openLog.rawCheckOut : rawCheckOut || "";
  const isCheckedIn =
    Boolean(openLog) ||
    ["checked-in", "check-in", "checkin", "checkedin", "in", "active", "open", "started"].includes(normalizedStatus) ||
    (Boolean(effectiveRawCheckIn) && !effectiveRawCheckOut && !isExplicitlyCheckedOut);
  const activeDateKey = formatDateKey(activeRawDate);
  const visibleLogs = isCheckedIn && activeDateKey
    ? logs.filter((log) => log.dateKey !== activeDateKey)
    : logs;

  return {
    status: isCheckedIn ? "checked-in" : "checked-out",
    checkInTime: formatTime(effectiveRawCheckIn),
    checkInAt: effectiveRawCheckIn,
    checkOutTime: formatTime(effectiveRawCheckOut),
    checkOutAt: effectiveRawCheckOut,
    date: formatDate(activeRawDate),
    loggedDate: formatDate(activeRawDate),
    rawLoggedDate: activeRawDate,
    logs: visibleLogs,
  };
};

export const normalizeLeaveBalances = (payload) => {
  const responseJSON = parseJsonString(payload?.responseJSON);

  const balances = responseJSON && !Array.isArray(responseJSON) && typeof responseJSON === "object"
    ? [
        {
          id: "earned-leave",
          label: "Earned Leave",
          remaining: toNumber(responseJSON.remainingEarnedLeaves),
          assigned: toNumber(responseJSON.AssignedInEarnedLeaves),
          used: toNumber(responseJSON.lastUsedEarnedLeaves),
          lastUpdated: toNumber(responseJSON.lastUpdatedEarnedLeaves),
        },
        {
          id: "sick-leave",
          label: "Sick Leave",
          remaining: toNumber(responseJSON.remainingSickLeaves),
          assigned: toNumber(responseJSON.AssignedInSickLeaves),
          used: toNumber(responseJSON.lastUsedSickLeaves),
          lastUpdated: toNumber(responseJSON.lastUpdatedSickLeaves),
        },
        {
          id: "comp-off",
          label: "Comp Off",
          remaining: toNumber(responseJSON.remainingCompOffLeaves),
          assigned: toNumber(responseJSON.AssignedInCompOffLeaves),
          used: toNumber(responseJSON.lastUsedCompOffLeaves),
          lastUpdated: toNumber(responseJSON.lastUpdatedCompOffLeaves),
        },
      ]
        .map((balance) => ({
          ...balance,
          total: balance.assigned,
        }))
    : toArray(payload).map((balance, index) => {
        const assigned = toNumber(firstValue(balance, ["assigned", "credited", "total", "totalDays", "annualQuota", "Total__c"], 0));
        const remaining = toNumber(firstValue(balance, ["remaining", "available", "balance", "Balance__c"], 0));
        return {
          id: firstValue(balance, ["id", "leaveType", "type", "Id"], `leave-balance-${index}`),
          label: firstValue(balance, ["label", "leaveType", "type", "name", "Name"], "Leave"),
          remaining,
          assigned,
          used: toNumber(firstValue(balance, ["used", "usedDays"], Math.max(assigned - remaining, 0))),
          total: assigned,
        };
      });

  return applyEarnedLeaveStacking(balances).filter((balance) => !isLossOfPayBalance(balance));
};

const LEAVE_CREDIT_HISTORY_START_DATE = "2026-05-03";

const isLossOfPayCredit = (credit = {}) => {
  const leaveType = firstStringValue(credit, ["leaveType", "type", "Leave_Type__c"], "").trim().toLowerCase();
  return leaveType === "lop" || leaveType.includes("loss of pay");
};

const isLeaveCreditRow = (credit = {}) => {
  const recordType = firstStringValue(
    credit,
    ["recordTypeDeveloperName", "recordTypeName", "recordTypeLabel", "recordType", "RecordType.DeveloperName", "RecordType.Name"],
    ""
  )
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  return !recordType || recordType.includes("leave credit");
};

export const normalizeLeaveCredits = (payload) =>
  toArray(payload)
    .filter((credit) => isLeaveCreditRow(credit) && !isLossOfPayCredit(credit))
    .map((credit, index) => {
      const rawDate = firstValue(credit, ["creditedDate", "startDate", "Start_Date__c", "date", "CreatedDate"], "");
      const dateKey = formatDateKey(rawDate);
      return {
        id: firstValue(credit, ["id", "Id"], `leave-credit-${index}`),
        creditNumber: firstValue(credit, ["creditNumber", "requestNumber", "Name"], ""),
        leaveType: firstValue(credit, ["leaveType", "type", "Leave_Type__c"], "Leave"),
        creditedDays: toNumber(firstValue(credit, ["creditedDays", "leaveDays", "days", "Leave_Days__c"], 0)),
        date: formatDate(rawDate),
        dateKey,
        status: normalizeStatus(firstValue(credit, ["status", "Status__c"], "Approved")),
        movementType: "Credit",
        source: firstValue(credit, ["source", "Source_System__c"], ""),
        notes: firstValue(credit, ["notes", "Notes__c", "reason"], ""),
      };
    })
    .filter((credit) => credit.dateKey >= LEAVE_CREDIT_HISTORY_START_DATE)
    .sort((left, right) => right.dateKey.localeCompare(left.dateKey));

const isLeaveDebitRequest = (request = {}) => {
  const recordType = firstStringValue(
    request,
    [
      "recordTypeDeveloperName",
      "recordTypeName",
      "recordTypeLabel",
      "recordType",
      "RecordType.DeveloperName",
      "RecordType.Name",
    ],
    ""
  )
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  return !recordType || !recordType.includes("leave credit");
};

const toLeaveRequestArray = (payload) => {
  const rows = toArray(payload);
  if (rows.length) {
    return rows;
  }

  const candidates = [payload?.data?.request, payload?.request, payload?.data, payload];
  const directRequest = candidates.find((candidate) =>
    candidate &&
    typeof candidate === "object" &&
    !Array.isArray(candidate) &&
    ["id", "requestId", "Id", "startDate", "Start_Date__c", "leaveType", "Leave_Type__c"].some(
      (key) => candidate[key] !== undefined && candidate[key] !== null
    )
  );

  return directRequest ? [directRequest] : [];
};

export const normalizeLeaveRequests = (payload) =>
  toLeaveRequestArray(payload).filter(isLeaveDebitRequest).map((request, index) => {
    const status = normalizeStatus(firstValue(request, ["status", "Status__c"], "Pending"));
    const leaveDays = toNumber(firstValue(request, ["leaveDays", "days", "Duration__c"], 0));
    const rawDuration = firstValue(request, ["duration", "Duration_Type__c"], "");
    const daysLabel = leaveDays ? `${leaveDays} ${leaveDays === 1 ? "day" : "days"}` : "";
    const startDateValue = firstValue(request, ["date", "startDate", "fromDate", "Start_Date__c"]);
    const endDateValue = firstValue(request, ["endDate", "toDate", "End_Date__c"], startDateValue);
    const startDateKey = normalizeIsoDateKey(startDateValue);
    const endDateKey = normalizeIsoDateKey(endDateValue) || startDateKey;
    const description = firstValue(request, ["description", "Leave_Description__c", "reason", "Reason__c", "Reason_Of_Leave__c"], "");
    const canEditStatus = ["pending", "submitted", "draft"].some((editableStatus) =>
      String(status || "").toLowerCase().includes(editableStatus)
    );
    const canCancelStatus = String(status || "").toLowerCase().includes("pending");

    return {
      id: firstValue(request, ["id", "requestId", "Id"], `leave-request-${index}`),
      requestNumber: firstValue(request, ["requestNumber", "name", "Name"], `Request ${index + 1}`),
      employeeName: firstValue(request, ["employeeName", "Employee_Name__c"], ""),
      employeeId: firstValue(request, ["employeeId", "Contact__c"], ""),
      date: formatDate(startDateValue),
      endDate: formatDate(endDateValue),
      startDateKey,
      endDateKey,
      duration: [daysLabel, rawDuration].filter(Boolean).join(" · "),
      leaveDays,
      leaveType: firstValue(request, ["leaveType", "type", "Leave_Type__c"], "Leave"),
      reason: description,
      description,
      rejectionReason: firstValue(request, ["rejectionReason", "Rejection_Reason__c"], ""),
      oooAdded: firstValue(request, ["oooAdded", "OOO_Added__c"], false) === true,
      canEdit: firstValue(request, ["canEdit"], canEditStatus) === true,
      canCancel: canCancelStatus && firstValue(request, ["canCancel"], true) === true,
      canResubmit: false,
      status,
      statusTone: statusTone(status),
      movementType: "Debit",
    };
  });

export const normalizeLeaveTypes = (payload) =>
  toArray(payload?.leaveTypes || payload?.types || payload).map((leaveType, index) => {
    if (typeof leaveType === "string") {
      return {
        id: leaveType,
        label: leaveType,
        value: leaveType,
      };
    }
    const label = firstValue(leaveType, ["label", "name", "leaveType", "Name"], `Leave Type ${index + 1}`);
    return {
      id: firstValue(leaveType, ["id", "value", "leaveType", "Id"], label),
      label,
      value: firstValue(leaveType, ["value", "leaveType", "name", "Name"], label),
    };
  });

export const normalizeHolidays = (payload) => {
  return toArray(payload)
    .map((holiday, index) => {
      const rawDate = firstValue(holiday, ["date", "holidayDate", "Holiday_Date__c"], "");
      const dateValue = parseDateValue(rawDate);
      return {
        rawDate,
        dateKey: formatDateKey(rawDate),
        dateValue,
        date: formatDate(rawDate),
        name: firstValue(holiday, ["name", "title", "Name"], `Holiday ${index + 1}`),
      };
    })
    .sort((first, second) => {
      if (!first.dateValue) {
        return 1;
      }
      if (!second.dateValue) {
        return -1;
      }
      return first.dateValue - second.dateValue;
    })
    .map(({ date, dateKey, name, rawDate }) => ({ date, dateKey, name, rawDate }));
};

const formatCurrency = (value, currency = "INR") => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value ? String(value) : "₹0";
  }
  if (currency === "INR") {
    return `₹${numericValue.toLocaleString("en-IN")}`;
  }
  return `${currency} ${numericValue.toLocaleString("en-IN")}`;
};

const toLabelItems = (source = {}, labels = [], fallbackItems = []) =>
  labels.map(([label, keys]) => ({
    label,
    value: firstValue(
      source,
      keys,
      fallbackItems.find((item) => item.label === label)?.value || ""
    ),
  }));

const unwrapProfilePayload = (payload) => {
  const parsedPayload = parseJsonString(payload);
  if (parsedPayload !== payload) {
    return unwrapProfilePayload(parsedPayload);
  }

  return (
    parsedPayload?.data?.profile ||
    parsedPayload?.data?.employee ||
    parsedPayload?.profile ||
    parsedPayload?.employee ||
    parsedPayload?.data ||
    parsedPayload ||
    {}
  );
};

const PROJECT_COLOR_KEYS = [
  "color",
  "colour",
  "projectColor",
  "projectColour",
  "colorCode",
  "colourCode",
  "Color__c",
  "Project_Color__c",
  "Project_Colour__c",
  "Project_Color_Code__c",
  "Color_Code__c",
  "Colour_Code__c",
  "Project__r.Color__c",
];

const getProjectColor = (source = {}, fallback = "") =>
  firstStringValue(source, PROJECT_COLOR_KEYS, fallback);

export const normalizeProjects = (payload) =>
  toArray(payload?.projects || payload).map((project, index) => {
    const projectManager = project?.Project_Manager__r || project?.projectManager || project?.manager || {};
    const projectName = firstValue(project, ["name", "projectName", "title", "Title__c", "Name"], `Project ${index + 1}`);
    const projectColor = getProjectColor(project);
    const allocationId = firstValue(project, ["allocationId", "projectAllocationId", "Project_Allocation__c", "ANT_Project_Allocation__c"], "");
    const explicitActive = firstValue(project, ["isActive", "active", "Active__c"], undefined);
    const rawStatus = firstValue(project, ["status", "Status__c"], "");
    const statusText = String(rawStatus || "").toLowerCase();
    const isActive = explicitActive !== undefined
      ? toBoolean(explicitActive, true)
      : !["inactive", "closed", "completed", "cancelled", "canceled", "archived"].some((item) => statusText.includes(item));
    return {
      id: firstValue(project, ["id", "projectId", "Id"], `project-${index}`),
      name: projectName,
      title: projectName,
      code: firstValue(project, ["code", "projectCode", "Name"], ""),
      description: cleanRichTextComment(firstValue(project, ["description", "Description__c"], "")),
      status: rawStatus || (isActive ? "Active" : "Inactive"),
      color: projectColor,
      colour: projectColor,
      colorCode: projectColor,
      isActive,
      allocationId,
      projectAllocationId: allocationId,
      isAllocated: Boolean(allocationId || firstValue(project, ["isAllocated", "allocated", "hasAllocation"], false)),
      accountId: firstValue(project, ["accountId", "AccountId", "Account__c"], ""),
      accountName: firstValue(project, ["accountName", "AccountName"], ""),
      projectManagerId: firstValue(project, ["projectManagerId", "projectManagerContactId", "Project_Manager__c", "Manager__c"], firstValue(projectManager, ["id", "Id"], "")),
      projectManagerName: firstValue(project, ["projectManagerName", "managerName"], firstValue(projectManager, ["name", "Name"], "")),
      projectManagerEmail: firstValue(project, ["projectManagerEmail", "managerEmail"], firstValue(projectManager, ["email", "Email"], "")),
      isProjectManager: Boolean(firstValue(project, ["isProjectManager", "isManager"], false)),
    };
  });

export const normalizeProjectAccounts = (payload) =>
  toArray(payload?.accounts || payload).map((account, index) => {
    const name = firstValue(account, ["name", "accountName", "label", "Name"], `Account ${index + 1}`);
    return {
      id: firstValue(account, ["id", "accountId", "Id"], ""),
      accountId: firstValue(account, ["accountId", "id", "Id"], ""),
      name,
      label: name,
    };
  }).filter((account) => account.accountId && account.name);

const getTaskTitle = (task) =>
  firstValue(task, ["title", "taskTitle", "Title__c", "Title", "label", "taskName", "name", "Name"], "") ||
  firstValue(task?.Task__r, ["Title__c", "Title", "Name"], "") ||
  firstValue(task?.task, ["title", "Title__c", "name", "Name"], "");

export const normalizeTasks = (payload) =>
  toArray(payload?.tasks || payload).map((task, index) => {
    const project = task?.Project__r || task?.project || {};
    const projectColor = getProjectColor(task, getProjectColor(project));
    return {
      id: firstValue(task, ["id", "taskId", "Id"], `task-${index}`),
      name: getTaskTitle(task) || `Task ${index + 1}`,
      projectId: firstValue(task, ["projectId", "Project__c"], firstValue(project, ["id", "Id"], "")),
      projectName: firstValue(task, ["projectName", "Project_Name__c", "Project__r.Project_Full_Name__c", "Project__r.Title__c", "Project__r.Name"], firstValue(project, ["projectFullName", "Project_Full_Name__c", "title", "Title__c", "name", "Name"], "")),
      projectColor,
      projectColour: projectColor,
      status: firstValue(task, ["status", "Status__c"], "Active"),
    };
  });

const normalizeProjectEntityStatus = (value) => {
  const status = String(value || "").trim();
  if (!status) {
    return "Defined";
  }
  const normalized = status.toLowerCase().replace(/[\s_-]/g, "");
  if (["todo", "to-do", "open", "new", "draft", "defined", "ready"].includes(normalized)) {
    return status === "To Do" ? "Defined" : status;
  }
  if (normalized.includes("progress")) {
    return "In Progress";
  }
  if (normalized.includes("review")) {
    return "Review";
  }
  if (normalized.includes("block")) {
    return "Blocked";
  }
  if (normalized.includes("prod")) {
    return "Prod";
  }
  if (normalized.includes("demo")) {
    return "Demoed";
  }
  if (normalized.includes("closed") || normalized.includes("done")) {
    return "Closed";
  }
  return status;
};

const normalizeMentionRows = (value) => {
  const parsedValue = parseJsonString(value);
  const mentionRows = toArray(parsedValue);
  const sourceRows =
    mentionRows.length || typeof parsedValue !== "string"
      ? mentionRows
      : parsedValue
          .split(/[;,]/)
          .map((item) => item.trim())
          .filter(Boolean);

  return sourceRows
    .map((mention) => {
      if (typeof mention === "string") {
        const cleaned = mention.replace(/^@/, "").trim();
        return {
          contactId: /^[a-zA-Z0-9]{15,18}$/.test(cleaned) ? cleaned : "",
          name: cleaned.includes("@") ? "" : cleaned,
          email: cleaned.includes("@") ? cleaned : "",
        };
      }
      return {
        contactId: firstValue(mention, ["contactId", "id", "Id"], ""),
        name: firstValue(mention, ["name", "label", "Name"], ""),
        email: firstValue(mention, ["email", "Email"], ""),
      };
    })
    .filter((mention) => mention.name || mention.email || mention.contactId);
};

const normalizeProjectEntityComments = (entity) =>
  toArray(
    entity?.comments ||
      entity?.projectComments ||
      entity?.Project_Entity_Commnets__r?.records ||
      entity?.Project_Entity_Comments__r?.records
  ).map((comment, index) => {
    return {
      id: firstValue(comment, ["id", "commentId", "Id"], `project-comment-${index}`),
      author: firstValue(comment, ["author", "createdBy", "commentedBy", "Commented_By__r.Name"], "") ||
        firstValue(comment?.Commented_By__r, ["name", "Name"], "") ||
        firstValue(comment?.CreatedBy, ["name", "Name"], "Team member"),
      authorEmail: firstValue(comment, ["authorEmail", "commentedByEmail", "Commented_By__r.Email"], "") ||
        firstValue(comment?.Commented_By__r, ["email", "Email"], ""),
      body: firstValue(comment, ["body", "comment", "message", "Comment__c"], ""),
      mentions: normalizeMentionRows(comment?.mentions || comment?.mentioned || comment?.Mentioned__c),
      attachments: normalizeProjectEntityAttachments(comment),
      createdAt: formatDate(firstValue(comment, ["createdAt", "createdDate", "CreatedDate"], "")) || "Recently",
    };
  });

const normalizeProjectEntityAttachments = (entity) =>
  toArray(
    entity?.attachments ||
      entity?.files ||
      entity?.documents ||
      entity?.ContentDocumentLinks?.records ||
      entity?.AttachedContentDocuments?.records
  ).map((file, index) => {
    const contentDocument = file?.ContentDocument || file?.contentDocument || {};
    const latestVersion = contentDocument?.LatestPublishedVersion || file?.LatestPublishedVersion || {};
    return {
      id: firstValue(file, ["id", "fileId", "contentDocumentId", "ContentDocumentId", "Id"], firstValue(contentDocument, ["id", "Id"], `file-${index}`)),
      fileId: firstValue(file, ["fileId", "contentDocumentId", "ContentDocumentId"], firstValue(contentDocument, ["id", "Id"], "")),
      versionId: firstValue(file, ["versionId", "ContentVersionId", "LatestPublishedVersionId"], firstValue(contentDocument, ["LatestPublishedVersionId"], firstValue(latestVersion, ["id", "Id"], ""))),
      name: firstValue(file, ["name", "fileName", "filename", "title", "Title"], firstValue(contentDocument, ["Title", "title", "Name"], `Attachment ${index + 1}`)),
      downloadUrl: firstValue(file, ["downloadUrl", "contentDownloadUrl", "publicUrl", "fileUrl", "url", "Download_URL__c"], ""),
      publicUrl: firstValue(file, ["publicUrl", "distributionPublicUrl", "downloadUrl", "fileUrl", "url"], ""),
      contentType: firstValue(file, ["contentType", "mimeType", "ContentType", "FileType"], firstValue(contentDocument, ["FileType"], "")),
      size: firstValue(file, ["size", "contentSize", "ContentSize"], firstValue(contentDocument, ["ContentSize"], "")),
      createdAt: formatDate(firstValue(file, ["createdAt", "createdDate", "CreatedDate"], firstValue(contentDocument, ["CreatedDate"], ""))),
    };
  });

const normalizeProjectEntityChecklist = (entity) =>
  toArray(
    entity?.checklist ||
      entity?.checklistItems ||
      entity?.projectChecklist ||
      entity?.Project_Entity_Checklists__r?.records
  )
    .map((item, index) => ({
      id: firstValue(item, ["id", "checklistItemId", "Id"], `project-checklist-${index}`),
      label: firstValue(item, ["label", "item", "title", "Label__c"], `Checklist item ${index + 1}`),
      completed: toBoolean(firstValue(item, ["completed", "isCompleted", "Completed__c"], false)),
      sortOrder: Number(firstValue(item, ["sortOrder", "order", "Sort_Order__c"], index + 1)) || index + 1,
      completedBy: firstValue(item, ["completedBy", "completedByName", "Completed_By__r.Name"], "") ||
        firstValue(item?.Completed_By__r, ["name", "Name"], ""),
      completedById: firstValue(item, ["completedById", "Completed_By__c"], ""),
      completedAt: formatDate(firstValue(item, ["completedAt", "Completed_At__c"], "")),
      createdAt: formatDate(firstValue(item, ["createdAt", "CreatedDate"], "")),
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder);

const toProjectEntityRows = (payload) => {
  const directEntity = payload?.entity || payload?.projectEntity || payload?.data?.entity || payload?.data?.projectEntity;
  if (directEntity && typeof directEntity === "object" && !Array.isArray(directEntity)) {
    return [directEntity];
  }

  return toArray(directEntity || payload?.entities || payload?.projectEntities || payload?.tasks || payload);
};

export const normalizeProjectEntities = (payload) =>
  toProjectEntityRows(payload).map((entity, index) => {
    const title = getTaskTitle(entity) || `Project entity ${index + 1}`;
    const project = entity?.Project__r || entity?.project || {};
    const assignee =
      entity?.Assigned_To__r ||
      entity?.assignedToUser ||
      (entity?.assignee && typeof entity.assignee === "object" ? entity.assignee : null) ||
      (entity?.assignedTo && typeof entity.assignedTo === "object" ? entity.assignedTo : null) ||
      {};
    const recordType = entity?.RecordType || entity?.recordType || {};
    const projectText = typeof entity?.project === "string" ? entity.project : "";
    const assigneeText = typeof entity?.assignee === "string"
      ? entity.assignee
      : typeof entity?.assignedTo === "string"
        ? entity.assignedTo
        : "";
    const assignedTo =
      firstStringValue(entity, ["assignedTo", "assignedToName", "ownerName", "Assigned_To__r.Name", "Assigned_To__r.Full_Name__c"], "") ||
      assigneeText ||
      firstStringValue(assignee, ["name", "fullName", "Name", "Full_Name__c"], "Unassigned");
    const projectName =
      firstStringValue(entity, ["projectName", "Project_Name__c", "Project__r.Title__c", "Project__r.Name"], "") ||
      projectText ||
      firstStringValue(project, ["title", "Title__c", "name", "Name"], "Project");

    return {
      id: firstValue(entity, ["id", "entityId", "taskId", "Id"], `project-entity-${index}`),
      key: firstValue(entity, ["key", "entityKey", "Name", "name"], `ENT-${index + 1}`),
      title,
      projectId: firstValue(entity, ["projectId", "Project__c"], firstValue(project, ["id", "Id"], "")),
      projectCode: firstValue(entity, ["projectCode", "Project_Code__c", "Project__r.Name"], firstValue(project, ["code", "Name"], "")),
      projectName,
      projectColor: getProjectColor(entity, getProjectColor(project)),
      projectColour: getProjectColor(entity, getProjectColor(project)),
      projectStatus: firstValue(entity, ["projectStatus", "Project_Status__c"], firstValue(project, ["status", "Status__c"], "")),
      projectIsActive: firstValue(entity, ["projectIsActive", "projectActive", "Project_Active__c"], firstValue(project, ["isActive", "active", "Active__c"], undefined)),
      projectManagerId: firstValue(entity, ["projectManagerId", "projectManagerContactId", "Project_Manager__c"], firstValue(project, ["projectManagerId", "Project_Manager__c"], "")),
      projectManagerName: firstValue(entity, ["projectManagerName"], firstValue(project?.Project_Manager__r, ["name", "Name"], "")),
      isProjectManager: Boolean(firstValue(entity, ["isProjectManager", "isManager"], false)),
      description: firstValue(entity, ["description", "Description__c", "details"], ""),
      status: normalizeProjectEntityStatus(firstValue(entity, ["status", "Status__c"], "")),
      priority: firstValue(entity, ["priority", "Priority__c"], "Medium"),
      assignedTo: firstStringValue(entity, ["assignedTo", "assigneeName"], assignedTo),
      assignedToId: firstStringValue(entity, ["assignedToId", "assigneeId", "Assigned_To__c", "Assigned_To__r.Id"], firstStringValue(assignee, ["contactId", "id", "Id"], "")),
      assignedToContactId: firstStringValue(entity, ["assignedToContactId", "assigneeId", "Assigned_To__c", "Assigned_To__r.Id"], firstStringValue(assignee, ["contactId", "id", "Id"], "")),
      assignedToEmail: firstStringValue(entity, ["assignedToEmail", "assigneeEmail", "Assigned_To__r.Email", "Assigned_To__r.Work_Email__c"], firstStringValue(assignee, ["email", "workEmail", "Email", "Work_Email__c"], "")),
      reporter: firstValue(entity, ["reporter", "reporterName"], "") ||
        firstValue(entity?.Reporter__r, ["name", "Name"], ""),
      recordType: firstValue(entity, ["recordType", "type"], "") ||
        firstValue(recordType, ["name", "Name"], "Task"),
      dueDate: formatDate(firstValue(entity, ["dueDate", "Due_Date__c"], "")),
      closeDate: formatDate(firstValue(entity, ["closeDate", "Close_Date__c"], "")),
      estimatedHours: firstValue(entity, ["estimatedHours", "Estimated_Hours__c"], ""),
      actualHours: firstValue(entity, ["actualHours", "Actual_Hours__c"], ""),
      comments: normalizeProjectEntityComments(entity),
      attachments: normalizeProjectEntityAttachments(entity),
      checklist: normalizeProjectEntityChecklist(entity),
    };
  });

const normalizeRequestAttachments = (payload) =>
  toArray(payload).map((attachment, attachmentIndex) => ({
    id: firstValue(attachment, ["id", "fileId", "contentDocumentId", "ContentDocumentId"], `request-file-${attachmentIndex}`),
    fileId: firstValue(attachment, ["fileId", "id", "contentDocumentId", "ContentDocumentId"], ""),
    fileName: firstValue(attachment, ["fileName", "name", "title", "Title"], "Attachment"),
    fileType: firstValue(attachment, ["fileType", "FileType"], ""),
    size: firstValue(attachment, ["size", "ContentSize"], ""),
    updatedAt: formatDate(firstValue(attachment, ["updatedAt", "LastModifiedDate", "createdAt"], "")) || "",
  }));

export const normalizeRequestComments = (payload) =>
  toArray(
    payload?.comment
      ? [payload.comment]
      : payload?.data?.comment
        ? [payload.data.comment]
        : payload?.comments || payload?.data?.comments || payload
  ).map((comment, index) => ({
    id: firstValue(comment, ["id", "commentId", "Id"], `request-comment-${index}`),
    requestId: firstValue(comment, ["requestId", "ParentId"], ""),
    authorName: firstValue(comment, ["authorName", "author", "CreatedBy.Name"], "AWC HRM"),
    authorEmail: firstValue(comment, ["authorEmail", "CreatedBy.Email"], ""),
    body: firstValue(comment, ["body", "comment", "CommentBody"], ""),
    mentions: normalizeMentionRows(comment?.mentions || comment?.mentioned || comment?.Mentioned__c),
    createdAt: formatDate(firstValue(comment, ["createdAt", "CreatedDate"], "")) || "Recently",
    attachments: normalizeRequestAttachments(firstValue(comment, ["attachments"], [])),
  }));

export const normalizeRequests = (payload) =>
  toArray(
    payload?.request
      ? [payload.request]
      : payload?.data?.request
        ? [payload.data.request]
        : payload?.requests || payload?.data?.requests || payload?.data?.items || payload?.data || payload
  ).map((request, index) => {
    const ownerEmail = firstValue(request, ["ownerEmail", "Owner.Email"], "");
    const assignedToEmail = firstValue(request, ["assignedToEmail", "HRM_Assigned_To__r.Email"], "");
    const requesterEmail =
      firstValue(request, ["requesterEmail"], "") ||
      firstValue(request, ["raisedByEmail", "employeeEmail", "Contact.Email", "Contact__r.Email"], "");
    const requesterName =
      firstValue(request, ["requesterName"], "") ||
      firstValue(request, ["raisedByName", "employeeName", "Contact.Name", "Contact__r.Name"], "");

    return {
      id: firstValue(request, ["id", "requestId", "Id"], `request-${index}`),
      requestNumber: firstValue(request, ["requestNumber", "number", "caseNumber", "CaseNumber", "Name"], `REQ-${index + 1}`),
      subject: firstValue(request, ["subject", "Subject"], "Request"),
      description: firstValue(request, ["description", "Description"], ""),
      requestType: firstValue(request, ["requestType", "type", "Type"], "HR Support"),
      priority: firstValue(request, ["priority", "Priority"], "Medium"),
      status: firstValue(request, ["status", "Status"], "Open"),
      updated: formatDate(firstValue(request, ["updated", "updatedAt", "LastModifiedDate"], "")) || "Recently",
      createdAt: formatDate(firstValue(request, ["createdAt", "CreatedDate"], "")) || "",
      ownerId: firstValue(request, ["ownerId", "OwnerId"], ""),
      ownerName: canonicalCasePersonName(firstValue(request, ["ownerName", "Owner.Name"], ""), ownerEmail),
      ownerEmail,
      assignedToId: firstValue(request, ["assignedToId", "HRM_Assigned_To__c"], ""),
      assignedToName: canonicalCasePersonName(
        firstValue(request, ["assignedToName", "HRM_Assigned_To__r.Name"], ""),
        assignedToEmail
      ),
      assignedToEmail,
      requesterId: firstValue(request, ["requesterId", "ContactId"], ""),
      requesterName: canonicalCasePersonName(requesterName, requesterEmail),
      requesterEmail,
      canComment: firstValue(request, ["canComment"], true) !== false,
      canManage: firstValue(request, ["canManage"], false) === true,
      canReassign: firstValue(request, ["canReassign"], false) === true,
      canChangeStatus: firstValue(request, ["canChangeStatus"], false) === true,
      canEditDescription: firstValue(request, ["canEditDescription"], false) === true,
      isAssignedWork: firstValue(request, ["isAssignedWork"], false) === true,
      attachmentCount: Number(firstValue(request, ["attachmentCount"], 0)) || 0,
      commentCount: Number(firstValue(request, ["commentCount"], 0)) || 0,
      lastCommentPreview: cleanRichTextComment(firstValue(request, ["lastCommentPreview"], "")),
      lastCommentBy: firstValue(request, ["lastCommentBy"], ""),
      comments: normalizeRequestComments(firstValue(request, ["comments"], [])),
      attachments: normalizeRequestAttachments(firstValue(request, ["attachments", "requestAttachments"], [])),
    };
  });

export const normalizeExpenses = (payload) =>
  toArray(payload?.expenses || payload).map((expense, index) => {
    const attachments = normalizeRequestAttachments(firstValue(expense, ["attachments", "files", "receiptFiles"], []));
    const amountValue = toNumber(firstValue(expense, ["amount", "Amount__c", "Total_Amount__c", "Claim_Amount__c"], 0));
    const expenseDateValue = firstValue(expense, ["date", "expenseDate", "Expense_Date__c", "Pay_Date__c", "Transaction_Date__c", "createdAt", "CreatedDate"], "");
    const status = firstValue(expense, ["status", "Status__c"], "Draft");
    const normalizedStatus = String(status || "").trim().toLowerCase();
    const canEditStatus = ["draft", "submitted", "need more information"].includes(normalizedStatus);
    const canCancelStatus = ["pending", "pending approval", "submitted"].includes(normalizedStatus);
    return {
      id: firstValue(expense, ["id", "expenseId", "Id"], `expense-${index}`),
      title: firstValue(expense, ["title", "name", "Name", "subject", "description", "Description__c"], `Expense ${index + 1}`),
      category: firstValue(expense, ["category", "expenseType", "type", "Category__c", "Expense_Type__c"], "Expense"),
      amount: formatCurrency(amountValue, firstValue(expense, ["currency", "CurrencyIsoCode"], "INR")),
      amountValue,
      date: formatDate(expenseDateValue),
      dateKey: normalizeIsoDateKey(expenseDateValue),
      description: firstValue(expense, ["description", "Description__c"], ""),
      rejectionReason: firstValue(expense, ["rejectionReason", "Rejection_Reason__c"], ""),
      status,
      canEdit: firstValue(expense, ["canEdit"], canEditStatus) === true,
      canCancel: canCancelStatus && firstValue(expense, ["canCancel"], true) === true,
      canResubmit: firstValue(expense, ["canResubmit"], String(status).toLowerCase() === "need more information") === true,
      attachmentCount: Number(firstValue(expense, ["attachmentCount", "fileCount"], attachments.length)) || 0,
      attachments,
    };
  });

export const normalizeDirectory = (payload) =>
  toArray(payload?.employees || payload?.directory || payload).map((person, index) => {
    const contactId = firstValue(person, ["contactId", "employeeSalesforceId", "salesforceContactId", "Contact__c", "Id"], "");
    const localId = firstValue(person, ["id", "employeeId", "userId"], "");
    return {
      id: contactId || localId || `person-${index}`,
      contactId,
      employeeId: firstValue(person, ["employeeId", "employeeNumber", "Employee_ID__c"], ""),
      employeeSalesforceId: contactId,
      name: firstValue(person, ["name", "fullName", "Name"], `Employee ${index + 1}`),
      role: firstValue(person, ["role", "designation", "title", "Designation__c"], "Employee"),
      team: firstValue(person, ["team", "department", "Department__c"], "Team"),
      email: firstValue(person, ["email", "workEmail", "Email"], ""),
      imageUrl: firstValue(person, ["imageUrl", "photoUrl", "profileImageUrl", "profilePhotoUrl", "Profile_Image_URL__c"], ""),
    };
  });

export const normalizeAssetAllocations = (payload) =>
  toArray(payload?.assets || payload?.allocations || payload).map((allocation, index) => {
    const asset = allocation?.asset || allocation?.Asset || allocation?.Asset__r || {};
    const assetTag =
      firstValue(allocation, ["assetTag", "tag", "assetCode", "Asset_Tag__c", "Asset_Code__c"], "") ||
      firstValue(asset, ["assetTag", "tag", "assetCode", "Asset_Tag__c", "Asset_Code__c", "Name"], `Asset ${index + 1}`);
    const name =
      firstValue(allocation, ["name", "assetName", "deviceName", "Asset_Name__c", "Device_Name__c"], "") ||
      firstValue(asset, ["name", "assetName", "deviceName", "Asset_Name__c", "Device_Name__c", "Name"], "Allocated asset");

    return {
      id: firstValue(allocation, ["id", "assetId", "allocationId", "Id", "Asset__c"], `asset-${index}`),
      assetTag,
      name,
      category:
        firstValue(allocation, ["category", "assetType", "type", "Asset_Type__c", "Category__c"], "") ||
        firstValue(asset, ["category", "assetType", "type", "Asset_Type__c", "Category__c"], "Asset"),
      serialNumber:
        firstValue(allocation, ["serialNumber", "serial", "Serial_Number__c", "Asset_Serial_Number__c"], "") ||
        firstValue(asset, ["serialNumber", "serial", "Serial_Number__c", "Asset_Serial_Number__c"], ""),
      assignedOn: formatDate(firstValue(allocation, ["assignedOn", "assignedDate", "Assigned_On__c", "Assigned_Date__c"], "")),
      returnDue:
        formatDate(firstValue(allocation, ["returnDue", "returnDate", "Return_Due__c", "Return_Date__c"], "")) ||
        firstValue(allocation, ["returnDueText"], "On exit"),
      condition:
        firstValue(allocation, ["condition", "Condition__c", "Asset_Condition__c"], "") ||
        firstValue(asset, ["condition", "Condition__c", "Asset_Condition__c"], ""),
      status: firstValue(allocation, ["status", "Status__c", "Allocation_Status__c"], "Assigned"),
    };
  });

const policyAcknowledgementKeys = [
  "acknowledged",
  "isAcknowledged",
  "hasAcknowledged",
  "hasAcknowledgement",
  "acknowledgementCreated",
  "acknowledgementSaved",
  "acknowledgedSuccessfully",
  "isAcked",
  "success",
  "Acknowledged__c",
  "Is_Acknowledged__c",
];

const policyAcknowledgementStatusKeys = [
  "acknowledgementStatus",
  "ackStatus",
  "acknowledgementState",
  "Acknowledgement_Status__c",
  "Ack_Status__c",
  "status",
  "Status__c",
];

const policyAcknowledgementDateKeys = [
  "acknowledgedAt",
  "acknowledgementDate",
  "acknowledgedDate",
  "acknowledgement.createdAt",
  "createdAt",
  "CreatedDate",
  "Acknowledged_At__c",
  "Acknowledgement_Date__c",
];

const policyAcknowledgementIdKeys = [
  "acknowledgementId",
  "ackId",
  "acknowledgement.id",
  "Acknowledgement__c",
];

const policyAcknowledgementResponseIdKeys = [
  ...policyAcknowledgementIdKeys,
  "id",
  "Id",
];

const resolvePolicyAcknowledged = (policy, fallback = false) => {
  const acknowledgementStatus = firstStringValue(policy, policyAcknowledgementStatusKeys, "").trim().toLowerCase();
  if (["acknowledged", "acknowledgement complete", "complete", "completed", "accepted", "signed"].includes(acknowledgementStatus)) {
    return true;
  }
  return toBoolean(firstValue(policy, policyAcknowledgementKeys, fallback), fallback);
};

const getPolicyStatus = (policy) => {
  if (policy.acknowledged) {
    return "Acknowledged";
  }
  if (policy.requiresAcknowledgement) {
    return "Acknowledgement due";
  }
  return firstValue(policy, ["status"], "Published") || "Published";
};

const normalizePolicy = (policy = {}, index = 0) => {
  const acknowledged = resolvePolicyAcknowledged(policy, false);
  const requiresAcknowledgement = toBoolean(
    firstValue(policy, ["requiresAcknowledgement", "ackRequired", "requiresAck", "Requires_Acknowledgement__c"], !acknowledged),
    !acknowledged
  );
  const effectiveDateValue = firstValue(policy, ["effectiveDate", "Effective_Date__c", "publishedDate", "Published_Date__c"], "");
  const updatedValue = firstValue(policy, ["updatedAt", "lastUpdated", "updated", "LastModifiedDate", "date"], effectiveDateValue);
  const normalizedPolicy = {
    id: firstValue(policy, ["id", "policyId", "Id"], `policy-${index}`),
    policyNumber: firstValue(policy, ["policyNumber", "number", "Name"], ""),
    title: firstValue(policy, ["title", "name", "policyName", "Title__c", "Name"], `Policy ${index + 1}`),
    category: firstValue(policy, ["category", "policyType", "type", "Category__c", "Policy_Type__c"], "General"),
    owner: firstValue(policy, ["owner", "ownerName", "department", "Department__c"], "HR"),
    version: firstValue(policy, ["version", "Version__c"], ""),
    effectiveDate: formatDate(effectiveDateValue),
    rawEffectiveDate: formatDateKey(effectiveDateValue),
    updated: formatDate(updatedValue) || firstValue(policy, ["date"], ""),
    summary: firstValue(policy, ["summary", "shortDescription", "Summary__c"], ""),
    body: firstValue(policy, ["body", "content", "description", "Description__c"], ""),
    documentUrl: firstValue(policy, ["documentUrl", "url", "fileUrl", "Document_URL__c"], ""),
    downloadUrl: firstValue(policy, ["downloadUrl", "downloadLink"], ""),
    fileId: firstValue(policy, ["fileId", "contentDocumentId", "ContentDocumentId"], ""),
    requiresAcknowledgement,
    acknowledged,
    acknowledgementId: firstValue(policy, policyAcknowledgementIdKeys, ""),
    acknowledgedAt: formatDate(firstValue(policy, policyAcknowledgementDateKeys, "")),
    status: firstValue(policy, ["status", "Status__c"], ""),
    tone: acknowledged ? "green" : requiresAcknowledgement ? "orange" : "blue",
  };

  return {
    ...normalizedPolicy,
    status: getPolicyStatus(normalizedPolicy),
  };
};

export const normalizePolicies = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const policyList = toArray(data.policies || data.items || data.records || data);
  const singlePolicy = firstValue(data, ["policy"], null);
  const source = policyList.length || !singlePolicy || Array.isArray(singlePolicy) ? policyList : [singlePolicy];
  return source.map(normalizePolicy);
};

export const normalizePolicyResponse = (payload, fallbackPolicy = {}) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const explicitPolicy = firstValue(data, ["policy"], null);
  const policy = explicitPolicy || data;
  const hasPolicyFields = Boolean(
    explicitPolicy ||
      firstValue(policy, ["title", "policyName", "Title__c", "Name", "category", "policyType"], "")
  );
  const mergedPolicy =
    policy && typeof policy === "object" && !Array.isArray(policy)
      ? hasPolicyFields
        ? { ...fallbackPolicy, ...policy }
        : {
            ...fallbackPolicy,
            acknowledged: resolvePolicyAcknowledged(policy, fallbackPolicy.acknowledged),
            acknowledgedAt: firstValue(policy, policyAcknowledgementDateKeys, fallbackPolicy.acknowledgedAt),
            acknowledgementId: firstValue(policy, policyAcknowledgementResponseIdKeys, fallbackPolicy.acknowledgementId),
          }
      : fallbackPolicy;
  return normalizePolicy(mergedPolicy, 0);
};

const normalizeLearningStatus = (value, acknowledged = false) => {
  if (acknowledged) {
    return "Acknowledged";
  }
  return normalizeStatus(value || "Assigned", "Assigned");
};

const getLearningAcknowledged = (course = {}, fallback = false) => {
  const acknowledgementStatus = firstStringValue(course, ["acknowledgementStatus", "ackStatus", "Acknowledgement_Status__c"], "").toLowerCase();
  const assignmentStatus = firstStringValue(course, ["status", "Status__c"], "").toLowerCase();
  if (["acknowledged", "accepted", "signed"].includes(acknowledgementStatus) || assignmentStatus === "acknowledged") {
    return true;
  }
  return toBoolean(firstValue(course, ["acknowledged", "isAcknowledged", "hasAcknowledged", "Acknowledged__c"], fallback), fallback);
};

const normalizeLearningCourse = (course = {}, index = 0) => {
  const acknowledged = getLearningAcknowledged(course, false);
  const status = normalizeLearningStatus(firstValue(course, ["status", "Status__c"], ""), acknowledged);
  const progress = Number(firstValue(course, ["progress", "Progress__c"], acknowledged ? 100 : 0));
  const proof = firstValue(course, ["proof", "Proof__c"], "");
  const proofUrl = firstValue(course, ["proofUrl", "Proof_URL__c"], "");
  const employeeComments = firstValue(course, ["employeeComments", "Employee_Comments__c"], "");
  const hasProof = toBoolean(firstValue(course, ["hasProof", "Has_Proof__c"], Boolean(proof || proofUrl)), Boolean(proof || proofUrl));
  const maxTimeHours = Number(firstValue(course, ["maxTimeHours", "maxTime", "durationHours", "Max_Time_Hours__c"], 0));
  return {
    id: firstValue(course, ["id", "trainingId", "Training__c", "Id"], `training-${index}`),
    assignmentId: firstValue(course, ["assignmentId", "trainingAssignmentId", "Training_Assignment__c", "Assignment__c"], firstValue(course, ["id", "Id"], `training-assignment-${index}`)),
    title: firstValue(course, ["title", "name", "trainingTitle", "Training_Title__c", "Name"], `Training ${index + 1}`),
    provider: firstValue(course, ["provider", "Provider__c"], "Antrazal Training"),
    category: firstValue(course, ["category", "assignmentCategory", "Assignment_Category__c", "Category__c"], "General"),
    progress: Number.isFinite(progress) ? progress : acknowledged ? 100 : 0,
    durationMinutes: firstValue(course, ["durationMinutes", "duration", "Duration_Minutes__c"], null),
    maxTimeHours: Number.isFinite(maxTimeHours) ? maxTimeHours : 0,
    status,
    assignedDate: formatDate(firstValue(course, ["assignedDate", "Assigned_Date__c", "CreatedDate"], "")),
    completedDate: formatDate(firstValue(course, ["completedDate", "Completed_Date__c"], "")),
    dueDate: formatDate(firstValue(course, ["dueDate", "Due_Date__c"], "")),
    requiresAcknowledgement: toBoolean(firstValue(course, ["requiresAcknowledgement", "requiresAck", "Requires_Acknowledgement__c"], true), true),
    acknowledged,
    acknowledgedAt: formatDate(firstValue(course, ["acknowledgedAt", "acknowledgedDate", "Acknowledged_At__c", "Acknowledgement_Date__c"], "")),
    hasProof,
    mentorApprovalStatus: firstValue(course, ["mentorApprovalStatus", "Mentor_Approval_Status__c"], "Pending Review"),
    mentorApprovedAt: formatDate(firstValue(course, ["mentorApprovedAt", "Mentor_Approved_At__c"], "")),
    acknowledgementId: firstValue(course, ["acknowledgementId", "Training_Acknowledgement__c", "Acknowledgement__c"], ""),
    content: firstValue(course, ["content", "trainingContent", "Content__c"], ""),
    description: firstValue(course, ["description", "shortDescription", "Description__c"], ""),
    courseUrl: firstValue(course, ["courseUrl", "Course_URL__c", "url"], ""),
    documentUrl: firstValue(course, ["documentUrl", "Document_URL__c", "downloadUrl"], ""),
    proof,
    proofUrl,
    employeeComments,
  };
};

export const normalizeLearning = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const courses = toArray(data.courses || data.learning || data.assignments || data.items || data.records || data).map(normalizeLearningCourse);
  const summary = data.summary || {};
  const acknowledgedCount = courses.filter((course) => course.acknowledged).length;
  const pendingCount = courses.filter((course) => course.requiresAcknowledgement && !course.acknowledged).length;
  return {
    summary: {
      courseCount: Number(firstValue(summary, ["courseCount", "total", "totalCount"], courses.length)) || courses.length,
      completedCount: Number(firstValue(summary, ["completedCount"], courses.filter((course) => course.status === "Completed" || course.status === "Acknowledged").length)) || 0,
      acknowledgedCount: Number(firstValue(summary, ["acknowledgedCount"], acknowledgedCount)) || acknowledgedCount,
      pendingAcknowledgementCount: Number(firstValue(summary, ["pendingAcknowledgementCount", "pendingCount"], pendingCount)) || pendingCount,
      hoursLeft: Number(firstValue(summary, ["hoursLeft"], 0)) || 0,
    },
    courses,
  };
};

export const normalizeLearningCourseResponse = (payload, fallbackCourse = {}) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const course = firstValue(data, ["course"], data);
  return normalizeLearningCourse({ ...fallbackCourse, ...(course || {}) }, 0);
};

const performanceScoreKeys = [
  "productiveHoursDaily",
  "challengerLevel",
  "softSkills",
  "accountability",
  "credibility",
  "learnability",
  "professionalism",
  "attentionToDetail",
  "proactiveness",
];

const toNullableScore = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const upperFirst = (value) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

const normalizePerformanceScores = (review = {}) => {
  const scoreSource = firstValue(review, ["scores", "Scores__c"], {}) || {};
  return performanceScoreKeys.reduce((scores, key) => {
    const fieldKeys = [key, upperFirst(key), `${key}__c`, `${upperFirst(key)}__c`];
    scores[key] = toNullableScore(
      firstValue(scoreSource, fieldKeys, undefined) ?? firstValue(review, fieldKeys, undefined)
    );
    return scores;
  }, {});
};

export const normalizePerformanceReview = (review = {}, index = 0) => {
  const scores = normalizePerformanceScores(review);
  const populatedScores = Object.values(scores).filter((score) => score !== null);
  const calculatedScore = populatedScores.length
    ? populatedScores.reduce((sum, score) => sum + score, 0) / populatedScores.length
    : null;
  const overallScore = toNullableScore(firstValue(review, ["overallScore", "Overall_Score__c"], undefined)) ?? calculatedScore;
  const reviewDate = firstValue(review, ["reviewDate", "Review_Date__c", "date"], "");

  return {
    id: firstValue(review, ["id", "reviewId", "Id"], `review-${index}`),
    reviewName: firstValue(review, ["reviewName", "name", "Name"], `Feedback Review ${index + 1}`),
    employeeId: firstValue(review, ["employeeId", "contactId", "Contact__c", "Employee__c"], ""),
    employeeName: firstValue(review, ["employeeName", "employee", "Employee_Name__c", "Contact__r.Name"], ""),
    reviewDate: formatDate(reviewDate),
    rawReviewDate: formatDateKey(reviewDate),
    status: firstValue(review, ["status", "Status__c"], "Amber"),
    overallScore,
    scores,
    descriptionVisible: toBoolean(firstValue(review, ["descriptionVisible", "showDescription"], false)),
    description: firstValue(review, ["description", "Description__c"], ""),
    meetingNotes: firstValue(review, ["meetingNotes", "Meeting_Notes__c"], ""),
    actionsToImprove: firstValue(review, ["actionsToImprove", "Actions_To_Improve__c"], ""),
    createdAt: formatDate(firstValue(review, ["createdAt", "CreatedDate"], "")),
    updatedAt: formatDate(firstValue(review, ["updatedAt", "LastModifiedDate"], "")),
  };
};

export const normalizePerformanceReviews = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  const reviews = toArray(data.reviews || data).map(normalizePerformanceReview);

  return {
    employeeId: firstValue(data, ["employeeId", "contactId"], ""),
    employeeName: firstValue(data, ["employeeName"], reviews[0]?.employeeName || ""),
    reviews,
    page: Number(firstValue(data, ["page"], 1)) || 1,
    pageSize: Number(firstValue(data, ["pageSize"], reviews.length || 50)) || 50,
    total: Number(firstValue(data, ["total"], reviews.length)) || reviews.length,
  };
};

export const normalizePerformanceReviewResponse = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : payload || {};
  return normalizePerformanceReview(firstValue(data, ["review"], data));
};

export const normalizePayrollSlips = (payload) =>
  filterEmployeePayrollHistory(toArray(payload?.slips || payload?.salarySlips || payload).map((slip, index) => {
    const currency = firstValue(slip, ["currency", "CurrencyIsoCode"], "INR");
    return {
      id: firstValue(slip, ["id", "slipId", "Id"], `slip-${index}`),
      period: firstValue(slip, ["period", "month", "name", "Name"], `Slip ${index + 1}`),
      periodKey: firstValue(slip, ["periodKey"], ""),
      payDate: formatDate(firstValue(slip, ["payDate", "paymentDate", "Pay_Date__c"], "")),
      gross: formatCurrency(firstValue(slip, ["gross", "grossSalary", "Gross__c"], 0), currency),
      deductions: formatCurrency(firstValue(slip, ["deductions", "Deduction__c"], 0), currency),
      tax: formatCurrency(firstValue(slip, ["tax", "tds", "Tax__c"], 0), currency),
      net: formatCurrency(firstValue(slip, ["net", "netPay", "Net__c"], 0), currency),
      status: firstValue(slip, ["status", "Status__c"], "Available"),
      payrollStatus: firstValue(slip, ["payrollStatus", "Status__c"], ""),
      publishedToHrm: toBoolean(firstValue(slip, ["publishedToHrm", "HRM_Published__c"], false)),
      downloadUrl: firstValue(slip, ["downloadUrl"], ""),
    };
  }));

const getDayKeyFromDate = (dateValue) => {
  const parsedDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][parsedDate.getDay()];
};

const TIMESHEET_DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const createCalendarWeeksForMonth = (monthId) => {
  const [year, month] = String(monthId || "").split("-").map(Number);
  if (!year || !month) {
    return [];
  }

  const monthName = new Date(year, month - 1, 1).toLocaleDateString("en-IN", { month: "short" });
  const monthNumber = String(month).padStart(2, "0");
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayMondayIndex = (new Date(year, month - 1, 1).getDay() + 6) % 7;
  const weeks = [];

  for (let weekStartDay = 1 - firstDayMondayIndex, index = 1; weekStartDay <= daysInMonth; weekStartDay += 7, index += 1) {
    const startDay = Math.max(1, weekStartDay);
    const endDay = Math.min(daysInMonth, weekStartDay + 6);
    const entries = {};

    TIMESHEET_DAY_KEYS.forEach((dayKey, dayIndex) => {
      const dayOfMonth = weekStartDay + dayIndex;
      const isOutsideMonth = dayOfMonth < 1 || dayOfMonth > daysInMonth;
      entries[dayKey] = {
        workDate: isOutsideMonth ? "" : `${year}-${monthNumber}-${String(dayOfMonth).padStart(2, "0")}`,
        status: "Draft",
        items: [],
        isOutsideMonth,
      };
    });

    weeks.push({
      id: `week_${year}_${monthNumber}_${index}`,
      label: `Week ${index}`,
      range: `${String(startDay).padStart(2, "0")}-${String(endDay).padStart(2, "0")} ${monthName}`,
      status: "Draft",
      entries,
    });
  }

  return weeks;
};

const findCalendarWeekForWorkDate = (weeks, workDate, dayKey) =>
  weeks.find((week) => week.entries?.[dayKey]?.workDate === workDate);

const REVIEW_COMMENT_KEYS = [
  "reviewComment",
  "reviewComments",
  "reviewNote",
  "reviewNotes",
  "rejectionReason",
  "rejectionComment",
  "rejectedReason",
  "managerComment",
  "approverComment",
  "approvalComment",
  "comments",
  "comment",
  "Review_Comment__c",
  "Review_Comments__c",
  "Rejection_Reason__c",
  "Rejection_Comment__c",
  "Approver_Comment__c",
  "Manager_Comment__c",
];

const cleanReviewCommentText = (value = "") => {
  const cleanedText = cleanRichTextComment(value)
    .replace(/^\*+\s*/, "")
    .replace(/\s+,/g, ",")
    .replace(/,\s*/g, ", ")
    .trim();

  return /^[*._\-\s]+$/.test(cleanedText) ? "" : cleanedText;
};

const normalizeReviewCommentValue = (value) => {
  if (value === undefined || value === null) {
    return "";
  }
  if (typeof value === "string" || typeof value === "number") {
    return cleanReviewCommentText(value);
  }
  if (Array.isArray(value)) {
    return value.map(normalizeReviewCommentValue).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    for (const key of REVIEW_COMMENT_KEYS.concat(["body", "message", "note", "reason", "text", "value"])) {
      const normalizedValue = normalizeReviewCommentValue(value[key]);
      if (normalizedValue) {
        return normalizedValue;
      }
    }
  }
  return "";
};

const normalizeReviewComment = (source) => {
  if (!source || typeof source !== "object") {
    return normalizeReviewCommentValue(source);
  }

  for (const key of REVIEW_COMMENT_KEYS) {
    const normalizedValue = normalizeReviewCommentValue(source[key]);
    if (normalizedValue) {
      return normalizedValue;
    }
  }
  return "";
};

const normalizeTimesheetStatus = (value, fallback = "Draft") => {
  const status = normalizeStatus(value, fallback);
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("review") || normalized.includes("submit") || normalized.includes("pending") || normalized.includes("approval")) {
    return "Submitted";
  }
  return status || fallback;
};

const isRejectedTimesheetStatus = (status) =>
  String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "") === "rejected";

const getTimeRowDate = (row, fallback = "") =>
  normalizeIsoDateKey(firstValue(row, [
    "workDate",
    "Work_Date__c",
    "WorkDate__c",
    "date",
    "Date__c",
    "timeCardDate",
    "Time_Card_Date__c",
    "TimeCard_Date__c",
    "timecardDate",
    "TSE_Date__c",
    "Timesheet_Date__c",
  ], fallback));

const TIME_ENTRY_ID_KEYS = [
  "timeEntryId",
  "timeSheetEntryId",
  "timesheetEntryId",
  "timeCardEntryId",
  "timecardEntryId",
  "entryId",
  "recordId",
  "salesforceId",
  "sfId",
  "Time_Entry__c",
  "Time_Sheet_Entry__c",
  "Time_Card_Entry__c",
  "Timesheet_Entry__c",
  "Timecard_Entry__c",
  "TSE__c",
  "timeItemId",
  "Id",
  "id",
];

const getTimeItemId = (item, index) =>
  firstValue(item, TIME_ENTRY_ID_KEYS, `time-item-${index}`);

const getTimeItemHours = (item = {}) =>
  toNumber(firstValue(item, ["hours", "Hours__c", "Working_Hours__c", "workingHours", "totalHours"], 0));

const isLeaveTimeItem = (item = {}) => {
  const explicitFlag = firstValue(
    item,
    ["isLeaveEntry", "isLeaveTimeEntry", "leaveEntry", "leaveTimeEntry", "isLeave", "leaveGenerated"],
    null
  );
  if (explicitFlag !== null && explicitFlag !== undefined && explicitFlag !== "") {
    return toBoolean(explicitFlag);
  }

  const descriptor = [
    firstStringValue(item, ["description", "notes", "workDescription", "Description__c", "Desc__c"], ""),
    firstStringValue(item, ["source", "Source__c", "entryType", "type", "recordType", "category"], ""),
  ].join(" ").toLowerCase();

  return (
    descriptor.includes("auto-created from approved leave") ||
    descriptor.includes("took leave") ||
    descriptor.includes("leave time entry")
  );
};

const normalizeTimeItems = (items, fallbackWorkDate = "", pathPrefix = "root") =>
  toArray(items).flatMap((item, index) => {
    const nestedItems = toArray(item.timeCardEntries || item.timeEntries || item.entries || item.items);
    const itemWorkDate = getTimeRowDate(item, fallbackWorkDate);
    const itemId = getTimeItemId(item, index);
    const rowKey = `${itemId || "time-item"}-${itemWorkDate || fallbackWorkDate || "date"}-${pathPrefix}-${index}`;
    if (nestedItems.length) {
      return normalizeTimeItems(nestedItems, itemWorkDate, rowKey);
    }

    const project = item.Project__r || item.project || {};
    const task = item.Project_Entity__r || item.Task__r || item.task || {};
    const workDate = getTimeRowDate(item, fallbackWorkDate);
    const projectColor = getProjectColor(item, getProjectColor(project));
    const normalizedStatus = normalizeTimesheetStatus(firstValue(item, ["status", "Status__c"], "Draft"), "Draft");
    const approvedHoursValue = firstValue(item, ["approvedHours", "approvedHoursValue", "Approved_Hours__c"], null);
    const isLeaveEntry = isLeaveTimeItem(item);
    const lockRows = isLeaveEntry || toBoolean(firstValue(item, ["lockRows", "Lock_Rows__c"], false));
    const readOnly = isLeaveEntry || lockRows || toBoolean(firstValue(item, ["readOnly", "isReadOnly"], false));

    return {
      id: itemId,
      timeEntryId: firstStringValue(item, TIME_ENTRY_ID_KEYS, itemId),
      recordId: firstStringValue(item, ["recordId", "Id", "id"], itemId),
      salesforceId: firstStringValue(item, ["salesforceId", "sfId", "Id", "id"], itemId),
      timeCardEntryId: firstStringValue(item, ["timeCardEntryId", "timecardEntryId", "Time_Card_Entry__c"], itemId),
      rowKey,
      clientRowId: rowKey,
      workDate,
      hours: String(firstValue(item, ["hours", "Hours__c", "Working_Hours__c", "workingHours", "totalHours"], "")),
      project:
        firstStringValue(item, ["projectName", "projectTitle", "projectAllocationName", "projAlName", "project", "Project_Name__c", "Project__r.Title__c", "Project__r.Name"], "") ||
        firstStringValue(project, ["title", "Title__c", "name", "Name"], ""),
      projectId: firstStringValue(item, ["projectId", "Project__c", "projectAllocationId", "projAlId"], firstStringValue(project, ["id", "Id"], "")),
      projectColor,
      projectColour: projectColor,
      task:
        getTaskTitle(item) ||
        firstStringValue(item, ["task", "taskName", "taskTitle", "projectEntityName", "projectEntityTitle", "peName", "Task_Name__c", "Project_Entity__r.Title__c", "Project_Entity__r.Name"], "") ||
        firstStringValue(task, ["title", "Title__c", "name", "Name"], ""),
      taskId: firstStringValue(item, ["taskId", "Task__c", "Project_Entity__c", "projectEntityId", "peId"], firstStringValue(task, ["id", "Id"], "")),
      description: firstValue(item, ["description", "notes", "workDescription", "Description__c", "Desc__c"], ""),
      status: normalizedStatus,
      approvedHours: approvedHoursValue === null || approvedHoursValue === "" ? "" : toNumber(approvedHoursValue),
      lockRows,
      isLeaveEntry,
      readOnly,
      canEdit: !readOnly && toBoolean(firstValue(item, ["canEdit", "Can_Edit__c"], true)),
      canSubmit: !readOnly && toBoolean(firstValue(item, ["canSubmit", "Can_Submit__c"], true)),
      reviewComment: isRejectedTimesheetStatus(normalizedStatus) ? normalizeReviewComment(item) : "",
    };
  });

const unwrapTimesheetPayload = (payload) =>
  (() => {
    const parsedPayload = parseJsonString(payload);
    if (parsedPayload !== payload) {
      return unwrapTimesheetPayload(parsedPayload);
    }
    if (Array.isArray(parsedPayload)) {
      return { entries: parsedPayload };
    }

    const parsedResponseJson = parseJsonString(parsedPayload?.responseJSON);
    if (parsedResponseJson !== parsedPayload?.responseJSON) {
      return Array.isArray(parsedResponseJson) ? { entries: parsedResponseJson } : unwrapTimesheetPayload(parsedResponseJson);
    }

    return (
      parsedPayload?.data?.timesheet ||
      parsedPayload?.data?.monthTimesheet ||
      parsedPayload?.timesheet ||
      parsedPayload?.monthTimesheet ||
      parsedPayload?.data ||
      parsedPayload ||
      {}
    );
  })();

const getTimesheetRows = (source) => {
  if (Array.isArray(source)) {
    return source;
  }

  const rowSource =
    source.days ||
    source.entries ||
    source.timeEntries ||
    source.timeCards ||
    source.timecards ||
    source.records ||
    source.items;

  if (rowSource && typeof rowSource === "object" && !Array.isArray(rowSource)) {
    return Object.entries(rowSource).map(([dayKey, day]) =>
      Array.isArray(day)
        ? { dayKey, items: day }
        : { dayKey, ...day }
    );
  }

  return toArray(rowSource || source);
};

const getTimesheetDayRowsFromWeek = (week) =>
  toArray(week.days || week.entries).length
    ? toArray(week.days || week.entries)
    : week.entries && typeof week.entries === "object"
      ? Object.entries(week.entries).map(([dayKey, day]) =>
          Array.isArray(day)
            ? { dayKey, items: day }
            : { dayKey, ...day }
        )
      : [];

const getTimesheetDayStatus = (day, weekStatus, items, existingItems = []) => {
  const statusItems = items.length ? [...existingItems, ...items] : existingItems;
  const leaveItems = statusItems.filter(isLeaveTimeItem);
  const workItems = statusItems.filter((item) => !isLeaveTimeItem(item));
  const statusSourceItems = workItems.length ? workItems : statusItems;
  const itemStatuses = statusSourceItems.map((item) => normalizeTimesheetStatus(item.status, "")).filter(Boolean);
  if (itemStatuses.includes("Rejected")) {
    return "Rejected";
  }
  if (itemStatuses.includes("Draft")) {
    return "Draft";
  }
  if (itemStatuses.includes("Submitted")) {
    return "Submitted";
  }
  if (!workItems.length && leaveItems.length) {
    const leaveHours = leaveItems.reduce((sum, item) => sum + getTimeItemHours(item), 0);
    if (leaveHours > 0 && leaveHours < 8) {
      return "Draft";
    }
  }
  if (itemStatuses.length && itemStatuses.every((status) => status === "Approved")) {
    return "Approved";
  }

  const explicitStatus = normalizeTimesheetStatus(firstValue(day, ["status", "Status__c"], ""), "");
  if (explicitStatus) {
    return explicitStatus;
  }

  return normalizeTimesheetStatus(weekStatus, "") || "Draft";
};

const getAggregateTimesheetStatus = (statuses = []) => {
  const normalizedStatuses = statuses.map((status) => normalizeTimesheetStatus(status, "")).filter(Boolean);
  if (normalizedStatuses.includes("Rejected")) {
    return "Rejected";
  }
  if (normalizedStatuses.includes("Submitted")) {
    return "Submitted";
  }
  if (normalizedStatuses.length && normalizedStatuses.every((status) => status === "Approved")) {
    return "Approved";
  }
  return normalizedStatuses.length ? "Draft" : "";
};

export const normalizeTimesheetMonth = (payload, monthId, fallbackMonth) => {
  const source = unwrapTimesheetPayload(payload);
  const sourceWeeks = toArray(source.weeks || source.timeWeeks);
  const sourceRows = getTimesheetRows(source);
  const hasCrmTimeRows = sourceWeeks.length > 0 || sourceRows.length > 0;
  const id = firstValue(source, ["id", "month"], monthId || fallbackMonth?.id || "");
  const calendarWeeks = createCalendarWeeksForMonth(id);

  const mergeDayRows = (dayRows, weekStatus = "") => {
    dayRows.forEach((day, dayIndex) => {
      const dayWorkDate = getTimeRowDate(day);
      const items = normalizeTimeItems(
        day.items ||
          day.entries ||
          day.timeEntries ||
          day.timeCardEntries ||
          (day.hours || day.project || day.projAlId || day.description ? [day] : []),
        dayWorkDate,
        `${weekStatus || "week"}-${dayWorkDate || firstValue(day, ["dayKey"], "day")}-${dayIndex}`
      );
      const workDate = dayWorkDate || items.find((item) => item.workDate)?.workDate || "";
      const dayKey = getDayKeyFromDate(workDate) || firstValue(day, ["dayKey"], "");
      if (!dayKey) {
        return;
      }
      const targetWeek = workDate && workDate.slice(0, 7) === id
        ? findCalendarWeekForWorkDate(calendarWeeks, workDate, dayKey)
        : null;
      if (!targetWeek) {
        return;
      }
      const existingEntry = targetWeek.entries[dayKey] || {};
      const normalizedStatus = getTimesheetDayStatus(
        day,
        weekStatus || existingEntry.status || "",
        items,
        existingEntry.items || []
      );
      targetWeek.entries[dayKey] = {
        ...existingEntry,
        workDate,
        status: normalizedStatus || existingEntry.status || "Draft",
        submittedOn: firstValue(day, ["submittedOn"], ""),
        reviewComment: isRejectedTimesheetStatus(normalizedStatus) ? normalizeReviewComment(day) : "",
        items: [
          ...(existingEntry.items || []),
          ...items.map((item) => ({ ...item, workDate: item.workDate || workDate })),
        ],
        isOutsideMonth: false,
      };
    });
  };

  sourceWeeks.forEach((week) => {
    mergeDayRows(getTimesheetDayRowsFromWeek(week), firstValue(week, ["status", "Status__c"], ""));
  });

  if (!sourceWeeks.length) {
    mergeDayRows(sourceRows);
  }

  const weeks = hasCrmTimeRows ? calendarWeeks : (calendarWeeks.length ? calendarWeeks : fallbackMonth?.weeks || []);
  const weeksWithStatus = weeks.map((week) => {
    const entryStatuses = Object.values(week.entries || {})
      .filter((entry) => !entry?.isOutsideMonth)
      .map((entry) => entry?.status || "Draft");
    return {
      ...week,
      status: getAggregateTimesheetStatus(entryStatuses) || week.status || "Draft",
    };
  });
  const aggregateStatus = getAggregateTimesheetStatus(weeksWithStatus.map((week) => week.status)) || "Draft";

  return {
    id,
    label: firstValue(source, ["label", "monthLabel"], fallbackMonth?.label || id),
    status: normalizeStatus(firstValue(source, ["status"], hasCrmTimeRows ? aggregateStatus : fallbackMonth?.status || "Draft")),
    totalHours: firstValue(source, ["totalHours", "hours", "totalLoggedHours"], ""),
    totalMinutes: firstValue(source, ["totalMinutes"], ""),
    approvedOn: firstValue(source, ["approvedOn"], ""),
    submittedOn: firstValue(source, ["submittedOn"], ""),
    weeks: weeksWithStatus.length ? weeksWithStatus : fallbackMonth?.weeks || [],
  };
};

const toObjectArray = (value) => {
  const arrayValue = toArray(value);
  if (arrayValue.length) {
    return arrayValue;
  }

  const parsedValue = parseJsonString(value);
  return parsedValue && typeof parsedValue === "object" && !Array.isArray(parsedValue) ? [parsedValue] : [];
};

const firstProfileArray = (...values) => values.map(toObjectArray).find((items) => items.length) || [];

const normalizeProfileAddressType = (value) => {
  const text = String(value || "").trim().toLowerCase();
  return text === "other" || text.includes("current") ? "Current Living Address" : "Mailing Address";
};

const normalizeProfileAddress = (address, index) => ({
  id: firstValue(address, ["id", "addressId", "Id"], `address-${index}`),
  type: normalizeProfileAddressType(firstValue(address, ["type", "addressType", "Address_Type__c"], "")),
  line1: firstValue(address, ["line1", "addressLine1", "street", "Street__c", "address"], ""),
  city: firstValue(address, ["city", "City__c"], ""),
  state: firstValue(address, ["state", "State__c"], ""),
  country: firstValue(address, ["country", "Country__c"], ""),
  zipCode: firstValue(address, ["zipCode", "postalCode", "pincode", "Postal_Code__c"], ""),
});

const normalizeProfileEmergencyContact = (contact, index) => ({
  id: firstValue(contact, ["id", "contactId", "emergencyContactId", "Id"], `emergency-${index}`),
  name: firstValue(contact, ["name", "fullName", "contactName", "Name", "Contact_Name__c"], ""),
  relationship: firstValue(contact, ["relationship", "Relationship__c"], "Other"),
  phone: firstValue(contact, ["phone", "mobile", "Phone__c", "Mobile__c"], ""),
  email: firstValue(contact, ["email", "Email__c"], ""),
});

const normalizeYesNo = (value) => {
  if (value === true) {
    return "Yes";
  }
  if (value === false || value === undefined || value === null || value === "") {
    return "No";
  }
  return String(value);
};

const normalizeProfileInsuranceDependent = (dependent, index) => ({
  id: firstValue(
    dependent,
    ["id", "relatedContactId", "relatedId", "relatedRecordId", "dependentId", "insuranceRelatedId", "RelatedContact__c", "Id"],
    `insurance-${index}`
  ),
  name: firstValue(dependent, ["name", "dependentName", "fullName", "Name", "Dependent_Name__c", "Contact_Name__c"], ""),
  relationship: firstValue(dependent, ["relationship", "Relationship__c"], "Other"),
  dateOfBirth: firstValue(dependent, ["dateOfBirth", "dob", "Date_Of_Birth__c", "Birthdate__c"], ""),
  coverage: firstValue(dependent, ["coverage", "coverageType", "insuranceType", "Coverage__c", "Insurance_Type__c"], "Health Insurance"),
  nominee: normalizeYesNo(firstValue(dependent, ["nominee", "isNominee", "Nominee__c"], false)),
});

const normalizeProfileBankAccount = (account, index) => ({
  id: firstValue(account, ["id", "bankAccountId", "Id"], `bank-${index}`),
  bankName: firstValue(account, ["bankName", "bank", "name", "Bank_Name__c"], ""),
  accountHolder: firstValue(account, ["accountHolder", "holderName", "accountHolderName", "Account_Holder_Name__c", "Account_Holder__c"], ""),
  accountNumber: firstValue(account, ["accountNumber", "bankAccount", "account", "Bank_Account_Number__c", "Account_Number__c"], ""),
  ifsc: firstValue(account, ["ifsc", "ifscCode", "IFSC_Code__c", "IFSC__c"], ""),
  accountType: firstValue(account, ["accountType", "type", "Type__c", "Account_Type__c"], ""),
  status: firstValue(account, ["status", "Status__c"], "Active"),
});

const normalizeProfileDocument = (document, index) => ({
  id: firstValue(document, ["id", "externalAttachmentId", "documentId", "externalAttachmentID", "ExternalAttachmentId", "Id"], `document-${index}`),
  externalAttachmentId: firstValue(document, ["externalAttachmentId", "documentId", "id", "ExternalAttachmentId", "Id"], ""),
  fileId: firstValue(document, ["fileId", "contentDocumentId", "ContentDocumentId", "ContentVersionId"], ""),
  contentDocumentId: firstValue(document, ["contentDocumentId", "fileId", "ContentDocumentId"], ""),
  documentName: firstValue(document, ["documentName", "documentType", "name", "title", "Title"], ""),
  label: firstValue(
    document,
    ["label", "documentType", "name", "title", "fileName", "filename", "Title", "ContentDocumentTitle"],
    `Document ${index + 1}`
  ),
  status: firstValue(document, ["status", "Status__c"], "Submitted"),
  downloadUrl: firstValue(document, ["downloadUrl", "fileUrl", "File_Link__c", "url"], ""),
  fileUrl: firstValue(document, ["fileUrl", "downloadUrl", "File_Link__c", "url"], ""),
  canDelete: toBoolean(firstValue(document, ["canDelete", "deletable"], false), false),
  updated: formatDate(firstValue(document, ["updatedAt", "updated", "lastModifiedDate", "LastModifiedDate"], "")),
});

const normalizeProfileDocumentUploadRequirement = (requirement, index) => {
  const source = typeof requirement === "string" ? { documentName: requirement, name: requirement } : requirement || {};
  const uploadedDocumentSource = source.document || source.uploadedDocument || source.file || null;
  const uploadedDocument = uploadedDocumentSource && typeof uploadedDocumentSource === "object"
    ? normalizeProfileDocument(uploadedDocumentSource, index)
    : null;
  const documentName = firstValue(
    source,
    ["documentName", "name", "label", "title"],
    uploadedDocument?.documentName || uploadedDocument?.label || `Document ${index + 1}`
  );
  const documentId = firstValue(
    source,
    ["documentId", "externalAttachmentId", "id", "ExternalAttachmentId", "Id"],
    uploadedDocument?.externalAttachmentId || uploadedDocument?.id || ""
  );
  const uploaded = toBoolean(firstValue(source, ["uploaded", "submitted"], Boolean(documentId || uploadedDocument)), Boolean(documentId || uploadedDocument));

  return {
    id: firstValue(source, ["id", "key"], documentName || `document-upload-${index}`),
    name: documentName,
    documentName,
    label: firstValue(source, ["label"], documentName),
    required: toBoolean(firstValue(source, ["required"], true), true),
    canUpload: toBoolean(firstValue(source, ["canUpload", "uploadable"], true), true),
    uploaded,
    status: firstValue(source, ["status"], uploaded ? "Submitted" : "Pending"),
    document: uploadedDocument,
    documentId,
    externalAttachmentId: documentId,
    downloadUrl: firstValue(source, ["downloadUrl", "fileUrl"], uploadedDocument?.downloadUrl || ""),
    fileUrl: firstValue(source, ["fileUrl", "downloadUrl"], uploadedDocument?.fileUrl || uploadedDocument?.downloadUrl || ""),
    canDelete: toBoolean(firstValue(source, ["canDelete"], uploadedDocument?.canDelete), Boolean(uploadedDocument?.canDelete)),
  };
};

const isProfileFieldComplete = (value) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "boolean") {
    return true;
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  const normalizedValue = String(value).trim().toLowerCase();
  return Boolean(normalizedValue && normalizedValue !== "-" && normalizedValue !== "not set" && normalizedValue !== "n/a");
};

const countDetailItems = (items = []) =>
  items.reduce(
    (summary, item) => ({
      completed: summary.completed + (isProfileFieldComplete(item.value) ? 1 : 0),
      total: summary.total + 1,
    }),
    { completed: 0, total: 0 }
  );

const countTableRows = (rows = [], fields = []) => {
  const rowList = rows.length ? rows : [{}];
  return rowList.reduce(
    (summary, row) => ({
      completed: summary.completed + fields.filter((field) => isProfileFieldComplete(row[field])).length,
      total: summary.total + fields.length,
    }),
    { completed: 0, total: 0 }
  );
};

const addProfileCompletionCounts = (...counts) =>
  counts.reduce(
    (summary, count) => ({
      completed: summary.completed + count.completed,
      total: summary.total + count.total,
    }),
    { completed: 0, total: 0 }
  );

const calculateProfileCompleteness = (profile) => {
  const summaryCounts = {
    completed: ["employeeId", "department", "location", "joinedOn"].filter((field) => isProfileFieldComplete(profile.summary?.[field])).length,
    total: 4,
  };
  const counts = addProfileCompletionCounts(
    summaryCounts,
    countDetailItems(profile.personal),
    countDetailItems(profile.contact),
    countDetailItems(profile.employment),
    countDetailItems(profile.payrollIdentifiers),
    countTableRows(profile.addresses, ["type", "line1", "city", "state", "country", "zipCode"]),
    countTableRows(profile.emergencyContacts, ["name", "relationship", "phone", "email"]),
    countTableRows(profile.insuranceDependents, ["name", "relationship", "dateOfBirth", "coverage", "nominee"]),
    countTableRows(profile.bankAccounts, ["bankName", "accountHolder", "accountNumber", "ifsc", "accountType", "status"])
  );

  return counts.total ? `${Math.round((counts.completed / counts.total) * 100)}%` : "0%";
};

const titleizeField = (value) =>
  String(value || "")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (letter) => letter.toUpperCase())
    .trim();

const missingDetailSuggestions = (items = [], sectionLabel) =>
  items
    .filter((item) => !isProfileFieldComplete(item.value))
    .map((item) => `Complete ${sectionLabel}: ${item.label}.`);

const missingTableSuggestions = (rows = [], fields = [], sectionLabel, emptyMessage) => {
  if (!rows.length) {
    return [emptyMessage];
  }

  return rows.flatMap((row, index) => {
    const missingFields = fields.filter((field) => !isProfileFieldComplete(row[field]));
    if (!missingFields.length) {
      return [];
    }

    const rowLabel = row.type || row.relationship || row.name || row.bankName || `row ${index + 1}`;
    return [`Complete ${sectionLabel} (${rowLabel}): ${missingFields.map(titleizeField).join(", ")}.`];
  });
};

const calculateProfileCompletionSuggestions = (profile) => {
  const suggestions = [];
  const summaryLabels = {
    employeeId: "Employee ID",
    department: "Department",
    location: "Work Location",
    joinedOn: "Joining Date",
  };

  Object.entries(summaryLabels).forEach(([field, label]) => {
    if (!isProfileFieldComplete(profile.summary?.[field])) {
      suggestions.push(`Ask HR to update ${label}.`);
    }
  });

  suggestions.push(
    ...missingDetailSuggestions(profile.personal, "personal details"),
    ...missingDetailSuggestions(profile.contact, "contact details"),
    ...missingDetailSuggestions(profile.employment, "employment details"),
    ...missingDetailSuggestions(profile.payrollIdentifiers, "payroll identifiers"),
    ...missingTableSuggestions(
      profile.addresses,
      ["type", "line1", "city", "state", "country", "zipCode"],
      "address",
      "Add mailing address details."
    ),
    ...missingTableSuggestions(
      profile.emergencyContacts,
      ["name", "relationship", "phone", "email"],
      "emergency contact",
      "Add an emergency contact."
    ),
    ...missingTableSuggestions(
      profile.insuranceDependents,
      ["name", "relationship", "dateOfBirth", "coverage", "nominee"],
      "insurance related",
      "Add insurance related details."
    ),
    ...missingTableSuggestions(
      profile.bankAccounts,
      ["bankName", "accountHolder", "accountNumber", "ifsc", "accountType", "status"],
      "bank details",
      "Ask finance to update bank details."
    )
  );

  return suggestions;
};

const PROFILE_SHAPE_KEYS = [
  "summary",
  "personal",
  "contact",
  "employment",
  "payrollIdentifiers",
  "addresses",
  "addressList",
  "employeeAddresses",
  "emergencyContacts",
  "insuranceRelated",
  "insuranceDependents",
  "bankAccounts",
  "documents",
  "documentUpload",
  "hrmPermissions",
  "editPermissions",
];

export const normalizeProfile = (payload, fallbackProfile = EMPTY_EMPLOYEE_PROFILE) => {
  const profile = unwrapProfilePayload(payload);
  const hasCrmProfile = Boolean(
    profile &&
      typeof profile === "object" &&
      !Array.isArray(profile) &&
      PROFILE_SHAPE_KEYS.some((key) => Object.prototype.hasOwnProperty.call(profile, key))
  );
  const profileFallback = hasCrmProfile
    ? {
        ...fallbackProfile,
        summary: {},
        personal: [],
        contact: [],
        employment: [],
        payrollIdentifiers: [],
        addresses: [],
        emergencyContacts: [],
        insuranceDependents: [],
        bankAccounts: [],
        documents: [],
        documentUpload: {},
        documentUploadRequirements: [],
        hrmPermissions: {},
        editPermissions: {},
        lockedFields: [],
      }
    : fallbackProfile;
  const summary = profile.summary || {};
  const personal = profile.personal || {};
  const contact = profile.contact || {};
  const employment = profile.employment || {};
  const payrollIdentifiers = profile.payrollIdentifiers || {};
  const employmentItems = Array.isArray(employment)
    ? employment
    : toLabelItems(employment, [
        ["Designation", ["designation"]],
        ["Manager", ["manager", "managerName"]],
        ["Employment Type", ["employmentType"]],
        ["Work Location", ["workLocation", "location"]],
        ["Shift", ["shift"]],
        ["Cost Center", ["costCenter"]],
      ], profileFallback.employment);
  const normalizedEmploymentItems = employmentItems;
  const employmentLocation = normalizedEmploymentItems.find((item) => item.label === "Work Location")?.value || "";
  const addresses = firstProfileArray(profile.addresses, profile.addressList, profile.employeeAddresses)
    .map(normalizeProfileAddress);
  const bankAccounts = firstProfileArray(profile.bankAccounts, profile.bankDetails, profile.banks, profile.employeeBanks)
    .map(normalizeProfileBankAccount);
  const emergencyContacts = firstProfileArray(profile.emergencyContacts, profile.emergencyContactList, profile.employeeEmergencyContacts)
    .map(normalizeProfileEmergencyContact);
  const insuranceDependents = firstProfileArray(profile.insuranceRelated, profile.insuranceDependents, profile.dependents)
    .map(normalizeProfileInsuranceDependent);
  const documents = firstProfileArray(
    profile.documents,
    profile.submittedDocuments,
    profile.employeeDocuments,
    profile.externalAttachments,
    profile.attachments,
    profile.contentDocuments,
    profile.files
  ).map(normalizeProfileDocument);
  const documentUploadSource = profile.documentUpload || profile.hrmPermissions || {};
  const hrmPermissions = profile.hrmPermissions || {};
  const editPermissions = profile.editPermissions || {};
  const lockedFields = toArray(profile.lockedFields);
  const rawDocumentUploadRequirements = firstProfileArray(
    profile.documentUploadRequirements,
    documentUploadSource.requiredDocuments,
    profile.requiredDocuments,
    profile.uploadRequirements
  );
  const fallbackAllowedDocumentRequirements = toArray(documentUploadSource.allowedDocuments).map((documentName) => ({
    documentName: typeof documentName === "string" ? documentName : firstValue(documentName, ["documentName", "name", "label"], ""),
    name: typeof documentName === "string" ? documentName : firstValue(documentName, ["documentName", "name", "label"], ""),
    canUpload: true,
  }));
  const documentUploadRequirements = (rawDocumentUploadRequirements.length ? rawDocumentUploadRequirements : fallbackAllowedDocumentRequirements)
    .map(normalizeProfileDocumentUploadRequirement)
    .filter((requirement) => requirement.documentName);
  const documentUploadEnabled = toBoolean(
    firstValue(documentUploadSource, ["enabled", "allowDocumentUpload", "onboarding"], documentUploadRequirements.length > 0),
    documentUploadRequirements.length > 0
  );

  const normalizedProfile = {
    ...profileFallback,
    summary: {
      ...profileFallback.summary,
      employeeId: firstValue(summary, ["employeeId", "employeeNumber"], profileFallback.summary.employeeId),
      department: firstValue(summary, ["department"], profileFallback.summary.department),
      location: firstValue(
        summary,
        ["location", "workLocation"],
        employmentLocation || firstValue(profile, ["location", "workLocation"], profileFallback.summary.location)
      ),
      joinedOn: formatDate(firstValue(summary, ["joinedOn"], profileFallback.summary.joinedOn)),
    },
    personal: Array.isArray(personal) ? personal : toLabelItems(personal, [
      ["Legal Name", ["legalName", "name"]],
      ["Preferred Name", ["preferredName"]],
      ["Date of Birth", ["dateOfBirth"]],
      ["Blood Group", ["bloodGroup"]],
      ["Gender", ["gender"]],
      ["Nationality", ["nationality"]],
    ], profileFallback.personal),
    contact: Array.isArray(contact) ? contact : toLabelItems(contact, [
      ["Work Email", ["workEmail", "email"]],
      ["Mobile", ["mobile", "phone"]],
      ["Personal Email", ["personalEmail"]],
      ["LinkedIn Profile", ["linkedinProfile", "linkedInProfile", "linkedinUrl", "Linkedin_Profile__c"]],
      ["Trailblazer Profile", ["trailblazerProfile", "trailblazerUrl", "Trailblazer_Profile__c"]],
    ], profileFallback.contact),
    employment: normalizedEmploymentItems,
    payrollIdentifiers: Array.isArray(payrollIdentifiers) ? payrollIdentifiers : toLabelItems(payrollIdentifiers, [
      ["PAN", ["pan"]],
      ["UAN", ["uan"]],
      ["Bank Account", ["bankAccount"]],
      ["IFSC", ["ifsc"]],
    ], profileFallback.payrollIdentifiers),
    addresses: (addresses.length ? addresses : profileFallback.addresses || []).map((address, index) => normalizeProfileAddress(address, index)),
    emergencyContacts: emergencyContacts.length ? emergencyContacts : profileFallback.emergencyContacts,
    insuranceDependents: insuranceDependents.length ? insuranceDependents : profileFallback.insuranceDependents,
    bankAccounts: bankAccounts.length ? bankAccounts : profileFallback.bankAccounts,
    documents: documents.length ? documents : profileFallback.documents,
    documentUpload: {
      ...(profileFallback.documentUpload || {}),
      ...documentUploadSource,
      enabled: documentUploadEnabled,
      requiredDocuments: documentUploadRequirements,
    },
    documentUploadRequirements: documentUploadRequirements.length ? documentUploadRequirements : profileFallback.documentUploadRequirements || [],
    hrmPermissions: { ...(profileFallback.hrmPermissions || {}), ...hrmPermissions },
    editPermissions: { ...(profileFallback.editPermissions || {}), ...editPermissions },
    lockedFields: lockedFields.length ? lockedFields : profileFallback.lockedFields || [],
  };

  return {
    ...normalizedProfile,
    summary: {
      ...normalizedProfile.summary,
      completeness: calculateProfileCompleteness(normalizedProfile),
      completionSuggestions: calculateProfileCompletionSuggestions(normalizedProfile),
    },
  };
};
